import { useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import MoreVertRounded from "@mui/icons-material/MoreVertRounded";
import DarkMode from "@mui/icons-material/DarkMode";
import LightMode from "@mui/icons-material/LightMode";
import ThumbsUpDownOutlined from "@mui/icons-material/ThumbsUpDownOutlined";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/store/auth-store";
import { useSignOutAny } from "@/lib/auth";
import { useThemeStore } from "@/store/theme-store";
import { useUiVersionStore } from "@/store/ui-version-store";
import { WhbLogo } from "@/components/WhbLogo";
import { AiAssistantPanel } from "@/components/AiAssistantPanel";
import { LiveChatButton } from "@/components/LiveChatButton";
import { FeedbackButton } from "@/components/FeedbackDialog";
import { HeaderSearch, HeaderSearchMobile } from "@/components/HeaderSearch";
import { LabeledAction } from "@/components/LabeledAction";
import { RealSessionBadge } from "@/components/RealSessionBadge";
import { mobileFullWidthMenuPaperSx } from "@/theme/mobile-menu-sx";

/**
 * Top app bar — WHB logo + wordmark on the left, global search in the
 * centre, Google-Ads-style action row on the right. Reading right→left
 * the cluster is:
 *
 *   Notifications · Help · Refresh · Feedback · LiveChat · AI Assistant
 *
 * On desktop each icon carries a small caption label underneath (the
 * `LabeledAction` wrapper handles the stacking). Mobile keeps the
 * high-touch actions on the bar — Search, Notifications, Live chat — and
 * collapses the rest (Refresh, theme toggle, Help, AI, Feedback) into an
 * overflow (⋮) menu. The theme toggle moved off the bar on 2026-04-17 so
 * Live chat could take its slot.
 */

const TOP_LEVEL_ROUTES = new Set([
  "/accounts",
  "/bots",
  "/marketplace",
  "/testing",
  "/lists",
  "/profile",
  "/help"
]);

/** Spacing between action icons on the desktop cluster (MUI units × 8px). */
const DESKTOP_ACTION_GAP = 1.25;

export function TopAppBar() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const uiVersion = useUiVersionStore((s) => s.uiVersion);
  const toggleUiVersion = useUiVersionStore((s) => s.toggle);
  const user = useAuthStore((s) => s.user);
  const signOutAny = useSignOutAny();

  const [aiOpen, setAiOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const moreOpen = !!moreAnchor;

  const initials = (user?.name ?? "?")
    .split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const firstName = user?.name?.split(" ")[0] ?? "Guest";

  // Hide back on first-level routes — going "back" from /accounts is not meaningful
  const canGoBack = !TOP_LEVEL_ROUTES.has(location.pathname);

  const handleRefresh = () => {
    // Simple approach: re-navigate to the same route. Pages that fetch data
    // in useEffect([location]) will refetch. Later we can wire a proper
    // query-invalidation pass via react-query.
    navigate(0);
  };

  return (
    <>
      <AppBar position="sticky" sx={{ boxShadow: "none" }}>
        <Toolbar sx={{ minHeight: { xs: 60, md: 72 }, px: { xs: 2, md: 4 }, gap: { xs: 1, md: 2 } }}>
          {/* Left: back arrow + brand */}
          {canGoBack && (
            <Tooltip title="Back">
              <IconButton onClick={() => navigate(-1)} aria-label="Back" edge="start">
                <ArrowBackRounded />
              </IconButton>
            </Tooltip>
          )}

          <Box
            onClick={() => navigate("/accounts")}
            role="button"
            tabIndex={0}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              ml: canGoBack ? 0.5 : 0,
              mr: { xs: 1, md: 2 },
              borderRadius: 2,
              px: 0.5,
              py: 0.5,
              "&:hover": { bgcolor: "action.hover" },
              transition: "background-color .15s ease",
              flexShrink: 0
            }}
          >
            <WhbLogo size={isDesktop ? 32 : 28} withWordmark={isDesktop} />
          </Box>

          {/* Centre: global search (desktop) or plain spacer (mobile).
           *  Desktop: the search is capped at 520px wide; the `flex: 1`
           *  spacer that follows absorbs leftover width so the action
           *  cluster is always pinned to the far right edge. */}
          {isDesktop ? (
            <>
              <HeaderSearch />
              <Box sx={{ flex: 1, minWidth: 16 }} />
            </>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          {/* Right: action cluster. Real-session badge leads the cluster so
           *  it's the first thing read right-to-left after the user pill —
           *  cheap, glance-able confirmation the WHB handoff worked. Mobile
           *  uses the compact (icon-only) variant to save bar space. */}
          <RealSessionBadge compact={!isDesktop} />
          <Stack
            direction="row"
            spacing={isDesktop ? DESKTOP_ACTION_GAP : 0.25}
            alignItems="center"
          >
            {isDesktop ? (
              <>
                {/* Order here is LEFT→RIGHT; user spec is "Right→Left:
                 *  Notifications, Help, Refresh, Feedback, LiveChat, AI".
                 *  So the visual right-most icon is Notifications and the
                 *  left-most is AI Assistant. */}
                <AiAssistantTrigger onClick={() => setAiOpen(true)} label="AI" />
                <LiveChatButton label="Chat" />
                <FeedbackButton label="Feedback" />

                <LabeledAction label="Refresh">
                  <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh} aria-label="Refresh page">
                      <RefreshRounded />
                    </IconButton>
                  </Tooltip>
                </LabeledAction>

                <LabeledAction label="Help">
                  <Tooltip title="Help">
                    <IconButton onClick={() => navigate("/help")} aria-label="Help">
                      <HelpOutlineRounded />
                    </IconButton>
                  </Tooltip>
                </LabeledAction>

                <LabeledAction label="Alerts">
                  <Tooltip title="Notifications">
                    <IconButton aria-label="Notifications">
                      <NotificationsNoneRounded />
                    </IconButton>
                  </Tooltip>
                </LabeledAction>
              </>
            ) : (
              <>
                <HeaderSearchMobile />
                <Tooltip title="Notifications">
                  <IconButton aria-label="Notifications">
                    <NotificationsNoneRounded />
                  </IconButton>
                </Tooltip>
                {/* Live chat replaces the theme toggle on mobile — the
                 *  toggle moved into the overflow menu between Refresh
                 *  and Help. */}
                <LiveChatButton />
                <Tooltip title="More">
                  <IconButton
                    onClick={(e) => setMoreAnchor(e.currentTarget)}
                    aria-label="More actions"
                  >
                    <MoreVertRounded />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Profile pill — desktop only, mobile uses bottom nav Profile tab */}
            {isDesktop && (
              <Tooltip title={`${user?.name ?? "Guest"} — Profile`}>
                <Box
                  onClick={() => navigate("/profile")}
                  role="button"
                  tabIndex={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    ml: 1,
                    pl: 0.5,
                    pr: 1.5,
                    py: 0.5,
                    borderRadius: 9999,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "background-color .15s ease"
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 800,
                      fontFamily: '"Roboto Flex Variable", sans-serif',
                      fontSize: "0.9375rem"
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ display: { md: "none", lg: "flex" }, flexDirection: "column", lineHeight: 1.1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {firstName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {user?.email ?? ""}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Mobile overflow menu. Order is: Refresh · theme toggle · Help ·
       *  (divider) · AI Assistant · Feedback & feature requests (Nolt) ·
       *  (divider) · Sign out. Live chat is NOT in here — it lives
       *  directly on the header bar on mobile. */}
      <Menu
        open={moreOpen}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: [
              { borderRadius: 3, minWidth: 240, mt: 0.5 },
              mobileFullWidthMenuPaperSx
            ]
          }
        }}
      >
        <MenuItem onClick={() => { handleRefresh(); setMoreAnchor(null); }}>
          <ListItemIcon><RefreshRounded fontSize="small" /></ListItemIcon>
          <ListItemText primary="Refresh" />
        </MenuItem>
        <MenuItem onClick={() => { toggleTheme(); setMoreAnchor(null); }}>
          <ListItemIcon>
            {mode === "dark"
              ? <LightMode fontSize="small" />
              : <DarkMode fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary={mode === "dark" ? "Light mode" : "Dark mode"} />
        </MenuItem>
        <MenuItem onClick={() => { navigate("/help"); setMoreAnchor(null); }}>
          <ListItemIcon><HelpOutlineRounded fontSize="small" /></ListItemIcon>
          <ListItemText primary="Help" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAiOpen(true); setMoreAnchor(null); }}>
          <ListItemIcon><AutoAwesomeRounded fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText
            primary="AI Assistant"
            secondary="Ask Claude about your portfolio and bots"
            secondaryTypographyProps={{ fontSize: "0.6875rem" }}
          />
          <Chip
            label="BETA"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 1, height: 18, fontSize: "0.625rem", fontWeight: 700 }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            // FeedbackButton isn't rendered on mobile, so the Nolt SDK's
            // hidden anchor isn't present. Open the public board in a new
            // tab instead — it's a mobile-friendly site and the round-trip
            // UX is the same as the desktop sidebar flow.
            window.open(
              "https://white-hat-bots.nolt.io",
              "_blank",
              "noopener,noreferrer"
            );
          }}
        >
          <ListItemIcon><ThumbsUpDownOutlined fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Feedback & feature requests"
            secondary="Post ideas on Nolt"
            secondaryTypographyProps={{ fontSize: "0.6875rem" }}
          />
        </MenuItem>
        <Divider />
        {/* UI version toggle — lets users opt into the V2 (Robinhood-style)
         *  redesign from the V1 header menu, and back out of V2 via the
         *  same label on the Profile page's TopAppBar overflow (the only
         *  TopAppBar that exists in V2). */}
        <MenuItem onClick={() => { toggleUiVersion(); setMoreAnchor(null); }}>
          <ListItemIcon><ScienceRounded fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText
            primary={uiVersion === "v1" ? "Try new design (V2)" : "Back to classic (V1)"}
            secondary={uiVersion === "v1"
              ? "Cleaner mobile layout — headerless pages, bigger chart"
              : "Restore the current mobile UI with the top header on every page"}
            secondaryTypographyProps={{ fontSize: "0.6875rem" }}
          />
          <Chip
            label={uiVersion === "v1" ? "NEW" : "V2"}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 1, height: 18, fontSize: "0.625rem", fontWeight: 700 }}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            signOutAny();
          }}
        >
          <ListItemIcon><LogoutRounded fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>

      {/* The AI panel lives at the root of the bar so it survives overflow menu close */}
      <AiAssistantPanel open={aiOpen} onClose={() => setAiOpen(false)} />
    </>
  );
}

/**
 * Sparkle-icon button with a BETA chip tucked on the top-right — mimics Google
 * Ads' "Ads Advisor BETA" trigger, matching the screenshot the user shared.
 * When `label` is set (desktop only) a caption shows under the icon.
 */
function AiAssistantTrigger({ onClick, label }: { onClick: () => void; label?: string }) {
  const trigger = (
    <Tooltip title="Ask Claude about your portfolio and bots.">
      <IconButton
        onClick={onClick}
        aria-label="Open AI Assistant"
        sx={{
          position: "relative",
          color: "primary.main",
          "&::after": {
            content: '"BETA"',
            position: "absolute",
            top: 0,
            right: 0,
            fontSize: "0.5625rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            lineHeight: 1,
            padding: "2px 4px",
            borderRadius: "6px",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            transform: "translate(25%, -15%)"
          }
        }}
      >
        <AutoAwesomeRounded />
      </IconButton>
    </Tooltip>
  );

  if (!label) return trigger;
  return <LabeledAction label={label}>{trigger}</LabeledAction>;
}
